import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  documents as docsApi,
  type CommentRow,
  type DocumentDetail,
} from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { CodeAwareBody } from "@/lib/codeRenderer";

import { QAShell } from "@/components/qa/QAShell";
import { VoteStack } from "@/components/qa/VoteStack";
import { AcceptedCheck } from "@/components/qa/AcceptedCheck";
import { AuthorChip } from "@/components/qa/AuthorChip";
import { TagList } from "@/components/qa/Tag";

/**
 * Question detail page for `kind=qa` documents. Rendered by
 * `Document.tsx` when it detects the kind; the route is still
 * `/d/:docId`.
 *
 * Layout: question header (title h1, body, tags, asker chip) on top,
 * "N answers" header, then each answer as a card with vote stack on
 * the left. Accepted answer pinned first (the server already does
 * this ordering inside `question_view`).
 *
 * Voting / accept buttons reuse the same REST endpoints + dispatcher
 * path as the non-qa `<VoteAndAcceptBar>`. The state is fetched twice
 * (question_view + doc detail) — the latter gives us the asker's
 * display name from the `members` array.
 */
export function QuestionPage({ doc }: { doc: DocumentDetail }) {
  const docId = doc.document.id;
  const qc = useQueryClient();
  const { state: authState } = useAuth();
  const isAuthed = authState.status === "authenticated";

  const viewQuery = useQuery({
    queryKey: ["question_view", docId],
    queryFn: () => docsApi.questionView(docId),
    staleTime: 10 * 1000,
  });

  // All comments on the doc — split by section_id below so we can render
  // comments under the question + each answer. `scope=all` includes resolved
  // ones; for qa we treat model-review comments as the substantive content,
  // not noise to hide.
  const commentsQuery = useQuery({
    queryKey: ["question_comments", docId],
    queryFn: () => docsApi.listComments(docId, "all"),
    staleTime: 10 * 1000,
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["question_view", docId] });
    void qc.invalidateQueries({ queryKey: ["question_comments", docId] });
    void qc.invalidateQueries({ queryKey: ["document", docId] });
  };

  if (viewQuery.isLoading) {
    return (
      <QAShell>
        <div className="text-sm-dense text-zinc-500 py-8">Loading question…</div>
      </QAShell>
    );
  }
  if (!viewQuery.data) {
    return (
      <QAShell>
        <div className="text-sm-dense text-zinc-500">
          Could not load this question.{" "}
          <Link to="/questions" className="text-accent hover:underline">
            Back to questions
          </Link>
        </div>
      </QAShell>
    );
  }

  const view = viewQuery.data;
  const askerMember =
    doc.members.find((m) => m.role === "owner") ?? doc.members[0];
  const askerName = askerMember?.user_id?.slice(0, 8) ?? "owner"; // members are redacted for anonymous; fall back gracefully
  const ownerDisplay =
    doc.document.role === "owner"
      ? authState.status === "authenticated"
        ? authState.user.display_name ?? authState.user.email.split("@")[0]
        : askerName
      : askerName;

  // Any signed-in user can vote on public Q&A docs (Stack-Overflow-style).
  // Backend enforces this — see `_require_member_or_public_contributor`.
  // For private docs the caller wouldn't reach this page if they weren't a
  // member (doc-detail would 403), so `isAuthed` alone is the right gate.
  const canVote = isAuthed;
  const isOwner = doc.document.role === "owner";

  // Group comments by section_id so each section can render its own thread.
  const commentsBySection: Record<string, CommentRow[]> = {};
  for (const c of commentsQuery.data?.comments ?? []) {
    (commentsBySection[c.section_id] ??= []).push(c);
  }
  const questionComments = commentsBySection[view.question.id] ?? [];

  return (
    <QAShell>
      <article>
        {/* ---- Question header ----
            Title source: the doc-level title (set when the user filed the
            question). The state-blob `outline_root.title` defaults to the
            literal "(root)" and is not authoritative — using `view.title`
            keeps the SPA consistent with the question feed. */}
        <header className="mb-4 pb-4 border-b border-zinc-200 dark:border-zinc-800">
          <h1 className="text-2xl font-semibold tracking-tight mb-2">
            {view.title || doc.document.title}
          </h1>
          <div className="flex items-center gap-4 text-xs-dense text-zinc-500">
            <span>Asked {relativeShort(doc.document.created_at)}</span>
            <span>
              Modified {relativeShort(doc.document.updated_at)}
            </span>
          </div>
        </header>

        {/* ---- Question body with vote stack ---- */}
        <section className="grid grid-cols-[44px_minmax(0,1fr)] gap-4 mb-6">
          <QuestionVoteStack
            docId={docId}
            sectionId={view.question.id}
            score={view.question.score}
            canVote={canVote}
            onMutated={refresh}
          />
          <div className="min-w-0">
            <article className="prose dark:prose-invert prose-zinc max-w-none">
              <CodeAwareBody body={view.question.body} />
            </article>
            <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
              <TagList tags={view.tags} size="sm" />
              <AuthorChip
                displayName={ownerDisplay}
                ts={doc.document.created_at}
                verb="asked"
              />
            </div>
            <CommentThread comments={questionComments} />
          </div>
        </section>

        {/* ---- Answers ---- */}
        <section>
          <h2 className="text-base-dense font-semibold mb-3 border-b border-zinc-200 dark:border-zinc-800 pb-2">
            {view.answers.length}{" "}
            {view.answers.length === 1 ? "Answer" : "Answers"}
          </h2>
          {view.answers.length === 0 ? (
            <div className="text-sm-dense text-zinc-500 py-4">
              No answers yet.
            </div>
          ) : (
            <ul className="space-y-6">
              {view.answers.map((a) => (
                <AnswerCard
                  key={a.id}
                  docId={docId}
                  answer={a}
                  isAccepted={a.id === view.accepted_answer_section_id}
                  canVote={canVote}
                  isOwner={isOwner}
                  onMutated={refresh}
                  comments={commentsBySection[a.id] ?? []}
                />
              ))}
            </ul>
          )}
        </section>

        {/* ---- Your answer (or sign-in prompt) ---- */}
        {isAuthed ? (
          <YourAnswerForm docId={docId} onPosted={refresh} />
        ) : (
          <div className="mt-8 p-4 border border-dashed border-zinc-200 dark:border-zinc-800 rounded-md text-sm-dense text-zinc-600 dark:text-zinc-400">
            <Link to="/login" className="text-accent hover:underline">
              Sign in
            </Link>{" "}
            to post an answer, or point your AI agent at this question via
            MCP (the <code>propose_answer</code> tool).
          </div>
        )}
      </article>
    </QAShell>
  );
}

// ---- Helpers --------------------------------------------------------------

function relativeShort(ts: string | null): string {
  if (!ts) return "";
  return new Date(ts).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function QuestionVoteStack({
  docId,
  sectionId,
  score,
  canVote,
  onMutated,
}: {
  docId: string;
  sectionId: string;
  score: number;
  canVote: boolean;
  onMutated: () => void;
}) {
  const up = useMutation({
    mutationFn: () => docsApi.endorseSection(docId, sectionId),
    onSuccess: onMutated,
  });
  const down = useMutation({
    mutationFn: () => docsApi.downvoteSection(docId, sectionId),
    onSuccess: onMutated,
  });
  return (
    <VoteStack
      score={score}
      // We don't (yet) get per-viewer vote state in question_view; the
      // server holds the source of truth and the post handlers are
      // toggle-aware. Leave the visual states neutral for v1.
      isUpvoted={false}
      isDownvoted={false}
      canVote={canVote}
      onUpvote={() => up.mutate()}
      onDownvote={() => down.mutate()}
      pending={up.isPending || down.isPending}
    />
  );
}

function AnswerCard({
  docId,
  answer,
  isAccepted,
  canVote,
  isOwner,
  onMutated,
  comments,
}: {
  docId: string;
  answer: {
    id: string;
    title: string;
    body: string;
    score: number;
    primary_author_party_id: string | null;
    author_kind: "agent" | "human" | "model";
    model_name: string | null;
  };
  isAccepted: boolean;
  canVote: boolean;
  isOwner: boolean;
  onMutated: () => void;
  comments: CommentRow[];
}) {
  const up = useMutation({
    mutationFn: () => docsApi.endorseSection(docId, answer.id),
    onSuccess: onMutated,
  });
  const down = useMutation({
    mutationFn: () => docsApi.downvoteSection(docId, answer.id),
    onSuccess: onMutated,
  });
  const accept = useMutation<
    { ok: true; accepted_answer_section_id: string | null },
    Error,
    void
  >({
    mutationFn: async () =>
      isAccepted
        ? await docsApi.unacceptAnswer(docId)
        : await docsApi.acceptAnswer(docId, answer.id),
    onSuccess: onMutated,
  });

  return (
    <li
      className={[
        "grid grid-cols-[44px_24px_minmax(0,1fr)] gap-3",
        isAccepted ? "border-l-2 border-emerald-500 pl-3 -ml-3" : "",
      ].join(" ")}
    >
      <VoteStack
        score={answer.score}
        isUpvoted={false}
        isDownvoted={false}
        canVote={canVote}
        onUpvote={() => up.mutate()}
        onDownvote={() => down.mutate()}
        pending={up.isPending || down.isPending}
      />
      <div className="pt-1">
        <AcceptedCheck accepted={isAccepted} />
      </div>
      <div className="min-w-0">
        <article className="prose dark:prose-invert prose-zinc max-w-none">
          <CodeAwareBody body={answer.body} />
        </article>
        <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
          <div>
            {isOwner && (
              <button
                type="button"
                onClick={() => accept.mutate()}
                disabled={accept.isPending}
                className={[
                  "text-xs-dense px-2.5 py-1 rounded font-medium transition",
                  isAccepted
                    ? "bg-emerald-600 text-white hover:bg-emerald-700"
                    : "border border-emerald-600 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/30",
                  "disabled:opacity-50",
                ].join(" ")}
              >
                {isAccepted ? "✓ Accepted" : "Accept this answer"}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {answer.author_kind === "model" && (
              <span className="px-1.5 py-0.5 rounded bg-accent/15 text-accent-fg dark:text-accent text-[10px] font-semibold uppercase tracking-wide">
                model
              </span>
            )}
            <AuthorChip
              displayName={
                answer.author_kind === "model"
                  ? answer.model_name ?? "Model"
                  : (answer.primary_author_party_id ?? "agent").slice(0, 8)
              }
              ts={null}
              verb="answered"
            />
          </div>
        </div>
        <CommentThread comments={comments} />
      </div>
    </li>
  );
}

function CommentThread({ comments }: { comments: CommentRow[] }) {
  if (comments.length === 0) return null;
  // Stable, oldest-first ordering; resolved ones sink slightly via opacity.
  const sorted = [...comments].sort((a, b) => a.ts.localeCompare(b.ts));
  return (
    <section className="mt-4 pt-3 border-t border-zinc-200 dark:border-zinc-800">
      <h3 className="text-xs-dense uppercase tracking-wide text-zinc-500 mb-2">
        {comments.length} {comments.length === 1 ? "Comment" : "Comments"}
      </h3>
      <ul className="space-y-3">
        {sorted.map((c) => (
          <CommentItem key={c.id} comment={c} />
        ))}
      </ul>
    </section>
  );
}

function CommentItem({ comment }: { comment: CommentRow }) {
  const isModel = comment.author_kind === "model";
  const author = isModel
    ? comment.model_name ?? "Model"
    : comment.author_party_id.slice(0, 8);
  return (
    <li
      className={[
        "rounded-md border p-3 text-sm-dense",
        isModel
          ? "border-accent/40 bg-accent/5"
          : "border-zinc-200 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-900/40",
        comment.resolved ? "opacity-60" : "",
      ].join(" ")}
    >
      <div className="flex items-center gap-2 mb-1 text-xs-dense text-zinc-600 dark:text-zinc-400">
        <span className="font-medium text-zinc-700 dark:text-zinc-300">
          {author}
        </span>
        {isModel && (
          <span className="px-1.5 py-0.5 rounded bg-accent/15 text-accent-fg dark:text-accent text-[10px] font-semibold uppercase tracking-wide">
            model
          </span>
        )}
        <span>·</span>
        <span>{relativeShort(comment.ts)}</span>
        {comment.resolved && (
          <>
            <span>·</span>
            <span className="italic">resolved</span>
          </>
        )}
      </div>
      <div className="min-w-0">
        <CodeAwareBody body={comment.body} />
      </div>
    </li>
  );
}

function YourAnswerForm({
  docId,
  onPosted,
}: {
  docId: string;
  onPosted: () => void;
}) {
  const [body, setBody] = useState("");
  const navigate = useNavigate();
  // propose_answer is an MCP tool, not a REST endpoint yet — humans go
  // through `append_section` via the dispatcher. The cleanest path
  // available today: redirect the human to the existing editor with a
  // hint to post their answer as a new section. (A dedicated REST
  // `/answer` endpoint is a small follow-up.)
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!body.trim()) return;
    onPosted();
    // For v1, send the user to the editor in collaborative-doc mode.
    // They can paste their answer there. A dedicated POST will follow.
    navigate(`/d/${docId}?write_answer=1`);
  };
  return (
    <section className="mt-10 border-t border-zinc-200 dark:border-zinc-800 pt-6">
      <h2 className="text-base-dense font-semibold mb-2">Your Answer</h2>
      <form onSubmit={handleSubmit}>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Write your answer in Markdown. Code blocks welcome."
          rows={8}
          className="w-full text-sm-dense border border-zinc-300 dark:border-zinc-700 rounded-md p-3 font-mono bg-white dark:bg-zinc-950 focus:outline-none focus:ring-1 focus:ring-accent"
        />
        <div className="mt-3 flex items-center gap-3">
          <button
            type="submit"
            disabled={!body.trim()}
            className="rounded-md bg-accent text-white px-4 py-2 text-sm-dense font-medium hover:opacity-90 disabled:opacity-40"
          >
            Post your answer
          </button>
          <span className="text-xs-dense text-zinc-500">
            You'll be taken to the editor to finalize.
          </span>
        </div>
      </form>
    </section>
  );
}
